import { addMonths, isAfter, isBefore, startOfMonth, subMonths } from 'date-fns';
import { ComponentProps, FC, ReactElement, useState } from 'react';

import { StyledDateCalendar, StyledRangeCalendars, StyledRangeColumn } from './DatePicker.styled';
import { formatWeekday } from './format-weekday';
import { type DateRange, nextRange } from './range-selection';
import { RangeCalendarHeader } from './RangeCalendarHeader';
import { RangeDay } from './RangeDay';

type CalendarPassthroughProps = Omit<
  ComponentProps<typeof StyledDateCalendar>,
  'value' | 'defaultValue' | 'onChange' | 'referenceDate' | 'slots' | 'slotProps'
>;

interface Props extends CalendarPassthroughProps {
  range: DateRange;
  onRangeChange: (_range: DateRange) => void;
}

type CalendarSlots = ComponentProps<typeof StyledDateCalendar>['slots'];
type CalendarSlotProps = ComponentProps<typeof StyledDateCalendar>['slotProps'];

const HiddenCalendarHeader = () => null;

export const RangeCalendar: FC<Props> = ({
  range,
  onRangeChange,
  minDate,
  maxDate,
  ...calendarProps
}) => {
  const [cursor, setCursor] = useState(() => startOfMonth(range.from ?? new Date()));
  const [hovered, setHovered] = useState<Date | null>(null);

  const secondMonth = addMonths(cursor, 1);

  const isPrevDisabled = minDate != null && !isAfter(startOfMonth(cursor), startOfMonth(minDate));
  const isNextDisabled =
    maxDate != null && !isBefore(startOfMonth(secondMonth), startOfMonth(maxDate));

  const handleDaySelect = (day: Date | null) => {
    if (day) onRangeChange(nextRange(range, day));
  };

  const column = (month: Date, header: ReactElement) => (
    <StyledRangeColumn>
      {header}
      {/*
       * `DateCalendar` owns its visible month internally, so remounting on the month
       * is what holds each side at its slot in the pair. The header above is a
       * sibling, not a `calendarHeader` slot, so the remount cannot steal its focus.
       */}
      <StyledDateCalendar
        key={month.toISOString()}
        referenceDate={month}
        value={null}
        onChange={handleDaySelect}
        minDate={minDate}
        maxDate={maxDate}
        dayOfWeekFormatter={formatWeekday}
        slots={
          {
            calendarHeader: HiddenCalendarHeader,
            day: RangeDay,
          } as unknown as CalendarSlots
        }
        slotProps={{ day: { range, hovered, onHover: setHovered } } as unknown as CalendarSlotProps}
        {...calendarProps}
      />
    </StyledRangeColumn>
  );

  return (
    <StyledRangeCalendars>
      {column(
        cursor,
        <RangeCalendarHeader
          month={cursor}
          onPrev={() => setCursor(subMonths(cursor, 1))}
          prevDisabled={isPrevDisabled}
        />,
      )}
      {column(
        secondMonth,
        <RangeCalendarHeader
          month={secondMonth}
          onNext={() => setCursor(addMonths(cursor, 1))}
          nextDisabled={isNextDisabled}
        />,
      )}
    </StyledRangeCalendars>
  );
};
